import { useState, type ReactElement } from 'react';
import type { PostFormatDto } from '@gr/core/content/posts/dto';
import type { OperationResult } from '@gr/utils/operation-result';
import { useServices, useT } from '../../../app/di';
import { useAction, useResource } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button } from '../../../ui-kit/Button/Button';
import { Card } from '../../../ui-kit/Card/Card';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { useBlog } from '../../accounts/workspaces';
import { NEW_POST_FORMAT, postFormatValuesOf, saveRequestOf, type PostFormatValues } from './model/post-formats';
import { PostFormatForm } from './PostFormatForm/PostFormatForm';
import { PostFormatList } from './PostFormatList/PostFormatList';

type FormatsResult = OperationResult<PostFormatDto[]>;

// POST-UC-10, POST-UC-10.CC-1: правила оформления блога в настройках.
export function PostFormatsSection(): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog } = useBlog();
  const confirm = useConfirm();
  const formats = useResource(() => api.posts.formats(blog.id), [api, blog.id]);
  const [editing, setEditing] = useState<PostFormatDto | 'new' | null>(null);
  const change = useAction(async (action: () => Promise<FormatsResult>) => action());

  const run = async (action: () => Promise<FormatsResult>): Promise<FormatsResult> => {
    const result = await change.run(action);
    if (result.success) formats.setData(result.data);
    return result;
  };

  const save = async (values: PostFormatValues): Promise<FormatsResult> => {
    const request = saveRequestOf(values);
    const result = editing === 'new' || editing === null ? await api.posts.createFormat(blog.id, request) : await api.posts.updateFormat(blog.id, editing.id, request);
    if (result.success) {
      formats.setData(result.data);
      setEditing(null);
    }
    return result;
  };

  const remove = async (format: PostFormatDto): Promise<void> => {
    const ok = await confirm({
      title: t('posts.formats.deleteConfirm', { name: format.name }),
      text: t('posts.formats.deleteText', { n: format.postCount }),
      confirmLabel: t('common.delete'),
      danger: true,
    });
    if (ok) await run(() => api.posts.deleteFormat(blog.id, format.id));
  };

  const add = (
    <Button icon="plus" onClick={() => setEditing('new')}>
      {t('posts.formats.add')}
    </Button>
  );
  const list = formats.data ?? [];

  return (
    <Card title={t('posts.formats.title')} actions={list.length > 0 ? add : undefined}>
      <div className="u-stack">
        <p className="u-muted u-small">{t('posts.formats.subtitle')}</p>
        {change.error ? <Alert tone="danger">{te(change.error)}</Alert> : null}
        {formats.error ? <Alert tone="danger">{te(formats.error)}</Alert> : null}
        {formats.loading && !formats.data ? (
          <Loading label={t('common.loading')} />
        ) : (
          <PostFormatList
            formats={list}
            disabled={change.pending}
            emptyAction={add}
            onEdit={(format) => setEditing(format)}
            onDelete={(format) => void remove(format)}
            onMakeDefault={(format) => void run(() => api.posts.updateFormat(blog.id, format.id, { name: format.name, rules: format.rules, isDefault: true }))}
          />
        )}
      </div>
      {editing ? (
        <Dialog
          open
          wide
          title={editing === 'new' ? t('posts.formats.newTitle') : t('posts.formats.editTitle', { name: editing.name })}
          closeLabel={t('common.close')}
          onClose={() => setEditing(null)}
        >
          <PostFormatForm
            initial={editing === 'new' ? { ...NEW_POST_FORMAT, isDefault: list.length === 0 } : postFormatValuesOf(editing)}
            defaultLocked={editing !== 'new' && editing.isDefault}
            submitLabel={t('posts.formats.save')}
            onSubmit={save}
            onCancel={() => setEditing(null)}
          />
        </Dialog>
      ) : null}
    </Card>
  );
}
